import React from 'react';

import { DataGrid } from '@gogame/data-grid';
import { useChildComponent } from '@gogame/hooks-ts';

import { useScreenState, useScreenDispatch } from '@gogame/screens';

import {
  EventFormCreate,
  EventFormEdit,
} from 'scenes/Events/components/EventForm';
import {
  COLUMN_APPLICATION,
  COLUMN_NAME,
  COLUMN_EVENT_TRIGGER,
  COLUMN_PERIOD,
  COLUMN_PURCHASE_DATA,
  COLUMN_GG_EVENT,
  COLUMN_UPDATED_DATE,
  COLUMN_CREATED_BY,
  COLUMN_CREATED_DATE,
  COMMON_EVENT_SOURCE_ID,
} from 'scenes/Events/constants';
import { useLoadEventsGrid } from 'scenes/Events/data';
import {
  SET_TABLE_SETTINGS,
  COLLAPSE_COLUMNS,
  REORDER_COLUMNS,
} from 'scenes/Events/screenReducer';
import EventActions from 'scenes/Events/components/Actions';
import { getPeriodValue } from 'scenes/Events/utils';

import { EventDetailsLink } from './EventDetailsLink';
import EventsDataTableToolbar from './EventsDataTableToolbar';
const EventsDataTable = () => {
  const [childComponent, openChildComponent] = useChildComponent();

  const screenState = useScreenState();
  const dispatch = useScreenDispatch();

  const { data, isLoading, isRefetching } = useLoadEventsGrid({
    filters: screenState.filters,
    tableSettings: screenState.tableSettings,
    searchQuery: screenState.searchQuery,
  });

  const handleCreate = () => {
    openChildComponent(EventFormCreate);
  };
  const handleEditAction = async ({ id }) => {
    openChildComponent(EventFormEdit, {
      id,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  return (
    <React.Fragment>
      {childComponent}
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        loading={isLoading}
        fetching={isRefetching}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        ready={screenState.ready}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={<EventsDataTableToolbar onCreate={handleCreate} />}
        storageKey={'events'}
        defaultColumnWidth={120}
        filtered={!!screenState.searchQuery}
      >
        <DataGrid.Column
          field={COLUMN_NAME}
          type={'link'}
          headerName={'Name'}
          width={260}
          fixed={true}
          resizable={false}
          linkComponent={EventDetailsLink}
          getTooltip={(v) => v}
        />
        <DataGrid.Column
          field={COLUMN_APPLICATION}
          type={'text'}
          headerName={'Application'}
          width={130}
          getValue={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_EVENT_TRIGGER}
          type={'text'}
          headerName={'Event Trigger'}
          width={230}
        />
        <DataGrid.Column
          field={COLUMN_PERIOD}
          type={'text'}
          headerName={'Period'}
          width={150}
          getValue={getPeriodValue}
        />
        <DataGrid.Column
          field={COLUMN_PURCHASE_DATA}
          type={'text'}
          headerName={'Purchase Data'}
          width={140}
        />
        <DataGrid.Column
          field={COLUMN_GG_EVENT}
          type={'text'}
          headerName={'GG Event Name'}
          width={170}
        />
        <DataGrid.Column
          field={COLUMN_UPDATED_DATE}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
          placeholder={'-'}
          headerName={'Last Updated'}
          width={160}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_BY}
          type={'text'}
          headerName={'Created by'}
          width={160}
          getValue={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_DATE}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
          placeholder={'-'}
          headerName={'Created'}
          width={160}
        />
        <DataGrid.Column
          actionsColDef
          type={'actions'}
          onEdit={handleEditAction}
          limit={2}
          actionsComponent={EventActions}
          isActionVisible={({ row }) => {
            return row?.sourceId === COMMON_EVENT_SOURCE_ID;
          }}
        />
      </DataGrid>
    </React.Fragment>
  );
};

export default EventsDataTable;
